"use client";

import { usePathname, useRouter } from "next/navigation";
import { useCallback, useRef } from "react";

function readCurrentTags({
  pendingTags,
  searchParams,
}: {
  pendingTags: string[] | null;
  searchParams: URLSearchParams;
}): string[] {
  return (
    pendingTags ?? searchParams.get("tags")?.split(",").filter(Boolean) ?? []
  );
}

/**
 * Manages tag toggle state with buffered pending refs to prevent race
 * conditions when toggling tags quickly (searchParams lags navigation).
 *
 * Returns `pendingTagsRef` so callers that need to read the buffered
 * tag state in other handlers (e.g. category select) can do so.
 */
export function useTagToggle({
  searchParams,
}: {
  searchParams: URLSearchParams;
}) {
  const pathname = usePathname();
  const router = useRouter();

  const pendingTagsRef = useRef<string[] | null>(null);
  const searchParamsKey = searchParams.toString();
  const lastSearchParamsKeyRef = useRef(searchParamsKey);

  if (lastSearchParamsKeyRef.current !== searchParamsKey) {
    lastSearchParamsKeyRef.current = searchParamsKey;
    pendingTagsRef.current = null;
  }

  const toggleTag = useCallback(
    (slug: string) => {
      const current = readCurrentTags({
        pendingTags: pendingTagsRef.current,
        searchParams,
      });

      const updated = current.includes(slug)
        ? current.filter((t) => t !== slug)
        : [...current, slug];

      pendingTagsRef.current = updated;

      const next = new URLSearchParams(searchParams);
      if (updated.length > 0) {
        next.set("tags", updated.join(","));
      } else {
        next.delete("tags");
      }

      const qs = next.toString();
      router.push(qs ? `${pathname}?${qs}` : pathname);
    },
    [pathname, router, searchParams]
  );

  return { pendingTagsRef, toggleTag };
}
